import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ChevronDown, Heart, ShieldCheck, Truck, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/ProductCard";
import TrustBar from "../components/TrustBar";
import SEO, { SITE_URL } from "../components/SEO";

const NoteSection = ({ label, notes }) => (
  <div className="py-3 border-b border-[rgba(212,175,55,0.12)]">
    <p className="eyebrow text-[0.6rem] mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {(notes || []).map((n) => <span key={n} className="text-sm text-[#FAFAF7]/80 bg-white/5 px-3 py-1 rounded-full">{n}</span>)}
    </div>
  </div>
);

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [volIdx, setVolIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [engraving, setEngraving] = useState("");
  const [showPyramid, setShowPyramid] = useState(true);
  const { addItem } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data); setActiveImg(0); setVolIdx(0); setQty(1); setEngraving("");
      api.get("/products", { params: { category: r.data.category } }).then((rr) =>
        setRelated(rr.data.filter((p) => p.id !== id).slice(0, 4)));
    }).catch(() => setProduct(false));
  }, [id]);

  if (product === false) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] text-[#FAFAF7]/60 pt-32">Fragrance not found.</div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] pt-32"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

  const vol = product.volumes?.[volIdx];
  const isBeauty = product.category === "Skincare" || product.category === "Soaps";
  const price = product.price + (vol?.price_delta || 0);

  const addToCart = () => {
    addItem({
      product_id: product.id, name: product.name, price, quantity: qty, subcategory: product.subcategory,
      volume: vol?.size, image: product.images?.[0], engraving: engraving.trim() || null,
    });
  };

  const toggleWishlist = async () => {
    if (!user) { toast.error("Please sign in to save fragrances"); return; }
    await api.post(`/account/wishlist/${product.id}`);
    toast.success("Wishlist updated");
  };

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO
        title={`${product.name} — ${product.brand}`}
        description={product.description?.slice(0, 155)}
        image={product.images?.[0]}
        path={`/product/${product.slug || product.id}`}
        type="product"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product",
          name: product.name, image: product.images, description: product.description,
          brand: { "@type": "Brand", name: product.brand },
          category: `${product.category} / ${product.subcategory}`,
          aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews_count },
          offers: {
            "@type": "Offer", priceCurrency: "USD", price: price,
            availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `${SITE_URL}/product/${product.slug || product.id}`,
          },
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="text-xs text-[#FAFAF7]/40 mb-8">
          <Link to="/shop" className="hover:text-[#D4AF37]">Shop</Link> / <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-[#D4AF37]">{product.category}</Link> / <span className="text-[#FAFAF7]/70">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="aspect-[4/5] rounded-xl overflow-hidden bg-[#141416] mb-4">
              <img src={product.images?.[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`w-20 h-24 rounded-lg overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[#D4AF37]" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="eyebrow mb-3">{product.brand} · {product.subcategory}</p>
            <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] leading-tight" data-testid="product-title">{product.name}</h1>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex gap-0.5">{Array(5).fill(0).map((_, i) => <Star key={i} size={15} className={i < Math.round(product.rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#FAFAF7]/20"} />)}</div>
              <span className="text-sm text-[#FAFAF7]/50">{product.rating} · {product.reviews_count} reviews</span>
            </div>
            <p className="font-serif-lux text-3xl text-[#D4AF37] mt-5" data-testid="product-price">{money(price)}</p>
            {product.subcategory === "Air Fresheners" && (
              <p data-testid="af-deal-badge" className="inline-flex items-center gap-2 mt-3 text-xs tracking-wide uppercase bg-[#58111A] text-[#FAFAF7] px-3 py-1.5 rounded-full">
                Deal · Any 3 Air Fresheners for $25 — applied automatically in your bag
              </p>
            )}
            <p className="text-[#FAFAF7]/65 leading-relaxed mt-5 font-light">{product.description}</p>

            {/* Volume */}
            {product.volumes?.length > 0 && (
              <div className="mt-7">
                <p className="eyebrow mb-3">Size</p>
                <div className="flex flex-wrap gap-3">
                  {product.volumes.map((v, i) => (
                    <button key={v.size} data-testid={`volume-${v.size}`} onClick={() => setVolIdx(i)}
                      className={`px-5 py-2.5 rounded-full text-sm border transition-all ${volIdx === i ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[rgba(212,175,55,0.25)] text-[#FAFAF7]/70 hover:border-[#D4AF37]/50"}`}>
                      {v.size}{v.price_delta > 0 && ` · +${money(v.price_delta)}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Engraving (perfumes only) */}
            {product.category === "Perfumes" && (
            <div className="mt-7">
              <p className="eyebrow mb-3">Personalise · Gold Engraving (optional)</p>
              <input data-testid="engraving-input" value={engraving} maxLength={20} onChange={(e) => setEngraving(e.target.value)}
                placeholder="Add up to 20 characters" className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
            </div>
            )}

            {/* Qty + Add */}
            <div className="flex gap-3 mt-7">
              <div className="flex items-center gap-4 gold-border rounded-full px-5">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-[#FAFAF7]/70 text-lg">−</button>
                <span className="text-[#FAFAF7] w-5 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="text-[#FAFAF7]/70 text-lg">+</button>
              </div>
              <button data-testid="add-to-cart-button" onClick={addToCart} className="btn-gold flex-1 py-3.5 rounded-full text-sm">Add to Bag</button>
              <button data-testid="wishlist-button" onClick={toggleWishlist} className="gold-border rounded-full w-14 flex items-center justify-center text-[#FAFAF7]/70 hover:text-[#58111A]"><Heart size={18} /></button>
            </div>

            {/* Trust */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-xs text-[#FAFAF7]/55">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#D4AF37]" /> 100% Authentic</span>
              <span className="flex items-center gap-1.5"><Truck size={14} className="text-[#D4AF37]" /> Free shipping over $150</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-[#D4AF37]" /> 2 complimentary samples</span>
            </div>

            {/* Scent Pyramid / Product Details */}
            <div className="mt-9 gold-border rounded-xl overflow-hidden">
              <button onClick={() => setShowPyramid((v) => !v)} data-testid="pyramid-toggle" className="w-full flex items-center justify-between px-5 py-4 bg-[#141416]">
                <span className="font-serif-lux text-xl text-[#FAFAF7]">{isBeauty ? "Product Details" : "Scent Pyramid"}</span>
                <ChevronDown size={18} className={`text-[#D4AF37] transition-transform ${showPyramid ? "rotate-180" : ""}`} />
              </button>
              {showPyramid && (
                <div className="px-5 pb-4">
                  <NoteSection label={isBeauty ? "Key Ingredients" : "Top Notes"} notes={product.notes?.top} />
                  <NoteSection label={isBeauty ? "Benefits" : "Heart Notes"} notes={product.notes?.heart} />
                  <NoteSection label={isBeauty ? "Skin Type" : "Base Notes"} notes={product.notes?.base} />
                </div>
              )}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-serif-lux text-3xl sm:text-4xl font-light text-[#FAFAF7]">You May Also Love</h2>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="text-sm text-[#D4AF37] link-underline inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
